import React, { useState } from 'react';
import { CheckCircle2, AlertCircle, Loader2, ArrowRight } from 'lucide-react';
import { Modal } from '../../../components/Modal.js';
import { Button } from '../../../components/Button.js';
import { LeadInput, LeadSchema } from '../types/lead.js';
import { submitLead } from '../api/leadApi.js';

interface LeadFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialSource?: string;
}

export const LeadFormModal: React.FC<LeadFormModalProps> = ({
  isOpen,
  onClose,
  initialSource = 'landing_page',
}) => {
  const [formData, setFormData] = useState<Partial<LeadInput>>({
    companyName: '',
    contactName: '',
    workEmail: '',
    phoneNumber: '',
    workspaceMembersCount: '51-200',
    inquiryType: initialSource.includes('doc') ? 'document' : 'poc',
    message: '',
    source: initialSource,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError(null);

    // バリデーション
    const result = LeadSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await submitLead(result.data);
      if (res.success) {
        setIsSuccess(true);
      } else {
        setServerError(res.message || '送信に失敗しました');
      }
    } catch {
      setServerError('予期せぬエラーが発生しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalClose = () => {
    setIsSuccess(false);
    setServerError(null);
    setErrors({});
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleModalClose}
      title={isSuccess ? 'お問い合わせ受付完了' : '無料PoC・デモ相談 / 資料請求'}
    >
      {isSuccess ? (
        <div className="text-center py-8 space-y-4">
          <div className="w-16 h-16 rounded-full bg-emerald-50 text-emerald-600 mx-auto flex items-center justify-center border border-emerald-200">
            <CheckCircle2 className="w-10 h-10" />
          </div>
          <h4 className="text-xl font-bold text-slate-900">お問い合わせを受け付けました</h4>
          <p className="text-sm text-slate-600 max-w-md mx-auto leading-relaxed">
            ご入力いただいたメールアドレス宛に確認メールを送信いたしました。
            担当のソリューションアーキテクトより、1営業日以内にご連絡いたします。
          </p>
          <div className="pt-4">
            <Button variant="primary" size="md" onClick={handleModalClose}>
              閉じる
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4 text-slate-800">
          {serverError && (
            <div className="p-3.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0 text-rose-600" />
              <span>{serverError}</span>
            </div>
          )}

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1.5">
              貴社名 <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              name="companyName"
              placeholder="例: 株式会社サンプル"
              value={formData.companyName || ''}
              onChange={handleChange}
              className={`w-full px-4 py-2.5 rounded-xl bg-white border text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 shadow-xs ${
                errors.companyName ? 'border-rose-400 focus:ring-rose-200' : 'border-slate-300 focus:ring-emerald-200 focus:border-emerald-500'
              }`}
            />
            {errors.companyName && <p className="text-rose-600 text-[11px] mt-1 font-medium">{errors.companyName}</p>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">
                ご担当者氏名 <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                name="contactName"
                placeholder="例: 山田 太郎"
                value={formData.contactName || ''}
                onChange={handleChange}
                className={`w-full px-4 py-2.5 rounded-xl bg-white border text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 shadow-xs ${
                  errors.contactName ? 'border-rose-400 focus:ring-rose-200' : 'border-slate-300 focus:ring-emerald-200 focus:border-emerald-500'
                }`}
              />
              {errors.contactName && <p className="text-rose-600 text-[11px] mt-1 font-medium">{errors.contactName}</p>}
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">
                企業メールアドレス <span className="text-rose-500">*</span>
              </label>
              <input
                type="email"
                name="workEmail"
                placeholder="name@company.co.jp"
                value={formData.workEmail || ''}
                onChange={handleChange}
                className={`w-full px-4 py-2.5 rounded-xl bg-white border text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 shadow-xs ${
                  errors.workEmail ? 'border-rose-400 focus:ring-rose-200' : 'border-slate-300 focus:ring-emerald-200 focus:border-emerald-500'
                }`}
              />
              {errors.workEmail && <p className="text-rose-600 text-[11px] mt-1 font-medium">{errors.workEmail}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">
                電話番号
              </label>
              <input
                type="tel"
                name="phoneNumber"
                placeholder="03-1234-5678"
                value={formData.phoneNumber || ''}
                onChange={handleChange}
                className={`w-full px-4 py-2.5 rounded-xl bg-white border text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 shadow-xs ${
                  errors.phoneNumber ? 'border-rose-400 focus:ring-rose-200' : 'border-slate-300 focus:ring-emerald-200 focus:border-emerald-500'
                }`}
              />
              {errors.phoneNumber && <p className="text-rose-600 text-[11px] mt-1 font-medium">{errors.phoneNumber}</p>}
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">
                Workspace アカウント (Member) 数 <span className="text-rose-500">*</span>
              </label>
              <select
                name="workspaceMembersCount"
                value={formData.workspaceMembersCount || '51-200'}
                onChange={handleChange}
                className="w-full px-4 py-2.5 rounded-xl bg-white border border-slate-300 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-500 shadow-xs"
              >
                <option value="1-50">1 〜 50 アカウント</option>
                <option value="51-200">51 〜 200 アカウント</option>
                <option value="201-1000">201 〜 1,000 アカウント</option>
                <option value="1001+">1,001 アカウント以上</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1.5">
              お問い合わせ種別 <span className="text-rose-500">*</span>
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                { id: 'poc', label: '無料PoC相談' },
                { id: 'demo', label: 'デモ実演希望' },
                { id: 'document', label: '資料・白書' },
                { id: 'other', label: 'その他相談' },
              ].map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setFormData((prev) => ({ ...prev, inquiryType: t.id as any }))}
                  className={`py-2 px-3 rounded-xl border text-xs font-semibold transition-all ${
                    formData.inquiryType === t.id
                      ? 'bg-emerald-50 border-emerald-500 text-emerald-800 shadow-xs ring-1 ring-emerald-500/20'
                      : 'bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1.5">
              ご相談・ご要望 (任意)
            </label>
            <textarea
              name="message"
              rows={3}
              placeholder="Google Vaultからの移行検討、退職者コストの削減試算、導入スケジュール等"
              value={formData.message || ''}
              onChange={handleChange}
              className="w-full px-4 py-2.5 rounded-xl bg-white border border-slate-300 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-500 shadow-xs"
            />
          </div>

          <div className="pt-2">
            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full justify-center text-sm font-bold shadow-md shadow-emerald-600/20"
              disabled={isSubmitting}
              icon={isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowRight className="w-5 h-5" />}
            >
              {isSubmitting ? '送信中...' : '同意して送信する'}
            </Button>
            <p className="text-[10px] text-slate-400 text-center mt-2.5">
              送信いただくことで、当社のプライバシーポリシーに同意したものとみなされます。
            </p>
          </div>
        </form>
      )}
    </Modal>
  );
};
